import * as Sentry from '@sentry/react';

export const SENTRY_ENABLED =
  import.meta.env.PROD &&
  import.meta.env.MODE === 'production' &&
  !!import.meta.env.VITE_SENTRY_DSN;

let initialized = false;

export function initSentry() {
  if (!SENTRY_ENABLED || initialized) return;
  const dsn = import.meta.env.VITE_SENTRY_DSN!;
  const release = import.meta.env.VITE_APP_VERSION;
  Sentry.init({
    dsn,
    release,
    environment: import.meta.env.MODE,
    sendDefaultPii: false,
    beforeSend(event, hint) {
      const error = hint.originalException;
      if (error instanceof Error && error.name === 'AbortError') return null;
      const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
      if (frames.some((f) => /(chrome|moz|safari-web)-extension:\/\//.test(f.filename ?? ''))) {
        return null;
      }
      const scrub = (s: string) =>
        s.replace(/(sk_|phc_|phx_|Bearer\s+)[A-Za-z0-9._-]+/g, '$1[REDACTED]');
      if (event.message) event.message = scrub(event.message);
      event.exception?.values?.forEach((v) => {
        if (v.value) v.value = scrub(v.value);
      });
      return event;
    },
  });
  initialized = true;
}

export interface SentryUser {
  id: string;
  email?: string;
}

export function setSentryUser(user: SentryUser, tags?: Record<string, string>) {
  if (!SENTRY_ENABLED) return;
  try {
    Sentry.setUser({ id: user.id, email: user.email });
    if (tags) {
      for (const [key, value] of Object.entries(tags)) {
        Sentry.setTag(key, value);
      }
    }
  } catch {
    /* silent */
  }
}

export function clearSentryUser() {
  if (!SENTRY_ENABLED) return;
  try {
    Sentry.setUser(null);
  } catch {
    /* silent */
  }
}

export function captureError(
  error: unknown,
  context?: { extra?: Record<string, unknown>; tags?: Record<string, string> },
) {
  if (!SENTRY_ENABLED) return;
  try {
    Sentry.captureException(error, {
      extra: context?.extra,
      tags: context?.tags,
    });
  } catch {
    /* silent */
  }
}
